import { useCallback } from 'react';
import { TextLongLeftIcon } from '@zep-us/zus/icons';
import { ICON_COLOR } from '@zep-us/zus/tokens';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'next-i18next';
import { logger } from '@zep/core-logger';
import { Tooltip } from '@/components/ui/Tooltip';
import { CheckSmallCircleIcon, MagicwandFillIcon, MessageLinesIcon } from '@/icons';
import { WithPlanBadge } from '@/shared/components/Badge/PlanBadge';
import { LocalizedOxTypeIcon } from '@/shared/components/LocalizedOxTypeIcon';
import { useUpsellingGuard, useUserPlanManager } from '@/shared/plan';
import { type QuizType } from '@/types/Quiz';
import { IconButton } from '@/ui_v3';
import { editingQuizIdAtom } from './focus/editModeAtom';
import { useOpenBuildAiModal } from './useOpenBuildAiModal';
import { useQuizList } from './useQuizList';
export const BuildQuizFloatingButtons = () => {
  const {
    t
  } = useTranslation(['build']);
  const {
    handleCreate,
    quizList
  } = useQuizList();
  const editingId = useAtomValue(editingQuizIdAtom);
  const handleCreateQuiz = useCallback((type: QuizType) => {
    logger.track('add_quizItem', {
      type,
      position: 'floating'
    });
    if (!editingId) {
      handleCreate(type);
      return;
    }
    const editIndex = quizList.findIndex(quiz => quiz.id === editingId);
    if (editIndex !== -1) {
      handleCreate(type, editIndex);
    }
  }, [editingId, handleCreate, quizList]);
  if (quizList.length === 0) return null;
  return <div className="border-black/8 fixed left-[calc(50%+336.5px)] top-[272px] z-30 hidden w-fit rounded-[16px] border bg-white px-sm py-[12px] shadow-xs tablet-lg:flex tablet-lg:flex-col tablet-lg:gap-xs" data-sentry-component="BuildQuizFloatingButtons" data-sentry-source-file="BuildQuizFloatingButtons.tsx">
      <div className="flex flex-col gap-4xs">
        <FloatingButton onClick={() => handleCreateQuiz('multiple')} tooltipContent={t('build.quizType.multiple')} defaultIcon={<CheckSmallCircleIcon size={'md'} />} data-sentry-element="FloatingButton" data-sentry-source-file="BuildQuizFloatingButtons.tsx" />
        <FloatingButton onClick={() => handleCreateQuiz('subjective')} tooltipContent={t('build.quizType.subjective')} defaultIcon={<MessageLinesIcon size={'md'} />} data-sentry-element="FloatingButton" data-sentry-source-file="BuildQuizFloatingButtons.tsx" />

        <FloatingButton onClick={() => handleCreateQuiz('ox')} tooltipContent={t('build.quizType.ox')} defaultIcon={<LocalizedOxTypeIcon size="md" />} data-sentry-element="FloatingButton" data-sentry-source-file="BuildQuizFloatingButtons.tsx" />
        <FloatingButton onClick={() => handleCreateQuiz('essay')} tooltipContent={t('build:build.quizType.essay')} defaultIcon={<TextLongLeftIcon size="md" />} data-sentry-element="FloatingButton" data-sentry-source-file="BuildQuizFloatingButtons.tsx" />
      </div>

      <div className="mx-auto h-[1px] w-xl border-y-[0.5px] border-alternative " />
      <FloatingAiBuildButton data-sentry-element="FloatingAiBuildButton" data-sentry-source-file="BuildQuizFloatingButtons.tsx" />
    </div>;
};
const FloatingAiBuildButton = () => {
  const {
    t
  } = useTranslation(['build']);
  const openBuildAiModal = useOpenBuildAiModal();
  const upsellingGuard = useUpsellingGuard();
  const {
    userPlanManager
  } = useUserPlanManager();
  const featurePlanType = userPlanManager.getFeaturePlanType('AI_FROM_QUERY');
  return <WithPlanBadge isBadgeVisible={true} planType={featurePlanType} data-sentry-element="WithPlanBadge" data-sentry-component="FloatingAiBuildButton" data-sentry-source-file="BuildQuizFloatingButtons.tsx">
      <FloatingButton onClick={() => upsellingGuard(() => openBuildAiModal('floating'), {
      feature: 'AI_FROM_QUERY',
      from: 'floating_ai_build_button'
    })} tooltipContent={t('build:build.ai.createQuiz')} defaultIcon={<MagicwandFillIcon size="md" fill={ICON_COLOR.default} />} data-sentry-element="FloatingButton" data-sentry-source-file="BuildQuizFloatingButtons.tsx" />
    </WithPlanBadge>;
};
const FloatingButton = ({
  onClick,
  defaultIcon,
  tooltipContent
}: {
  onClick: () => void;
  defaultIcon: React.JSX.Element;
  tooltipContent: string;
}) => {
  return <Tooltip side="right" arrow={{
    hide: true
  }} trigger={<IconButton variant={'transparent'} intent={'neutral'} size={'md'} onMouseDown={e => {
    e.stopPropagation();
    e.preventDefault();
  }} onClick={e => {
    e.stopPropagation();
    onClick();
  }}>
          {defaultIcon}
        </IconButton>} content={tooltipContent} data-sentry-element="Tooltip" data-sentry-component="FloatingButton" data-sentry-source-file="BuildQuizFloatingButtons.tsx" />;
};